import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import adminProductCategoryService from '../../services/adminProductCategoryService';
import BackButton from '../../components/BackButton';

const { TextArea } = Input;

const ProductCategoryList = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    const result = await adminProductCategoryService.getProductCategories();
    if (result.success) {
      setCategories(result.data);
    } else {
      message.error(result.error);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalVisible(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name_en: category.name_en,
      name_el: category.name_el,
      description_en: category.description_en,
      description_el: category.description_el,
      is_active: category.is_active
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    const result = await adminProductCategoryService.deleteProductCategory(id);
    if (result.success) {
      message.success(t('admin.productCategories.deleteSuccess'));
      fetchCategories();
    } else {
      message.error(result.error);
    }
  };

  const handleExport = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.productCategories.nameEn'), dataIndex: 'name_en' },
      { title: t('admin.productCategories.nameEl'), dataIndex: 'name_el' },
      { title: t('admin.productCategories.descriptionEn'), dataIndex: 'description_en' },
      { title: t('admin.productCategories.descriptionEl'), dataIndex: 'description_el' },
      {
        title: t('common.status'),
        dataIndex: 'is_active',
        exportRender: (val) => val ? t('common.active') : t('common.inactive')
      }
    ];
    exportToExcel(categories, exportColumns, 'product_categories', t('admin.productCategories.title'));
    message.success(t('admin.productCategories.exportSuccess'));
  };

  const handleSubmit = async (values) => {
    const result = editingCategory
      ? await adminProductCategoryService.updateProductCategory(editingCategory.id, values)
      : await adminProductCategoryService.createProductCategory(values);

    if (result.success) {
      message.success(editingCategory ? t('admin.productCategories.updateSuccess') : t('admin.productCategories.createSuccess'));
      setModalVisible(false);
      form.resetFields();
      fetchCategories();
    } else {
      message.error(result.error);
    }
  };

  const columns = [
    {
      title: t('admin.productCategories.nameEn'),
      dataIndex: 'name_en',
      key: 'name_en',
      sorter: (a, b) => a.name_en.localeCompare(b.name_en)
    },
    {
      title: t('admin.productCategories.nameEl'),
      dataIndex: 'name_el',
      key: 'name_el'
    },
    {
      title: t('admin.productCategories.descriptionEn'),
      dataIndex: 'description_en',
      key: 'description_en',
      ellipsis: true,
      width: 250
    },
    {
      title: t('common.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
      filters: [
        { text: t('common.active'), value: true },
        { text: t('common.inactive'), value: false }
      ],
      onFilter: (value, record) => record.is_active === value
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('admin.productCategories.deleteConfirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            okType="danger"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />
      <Card
        title={t('admin.productCategories.title')}
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!categories.length}
            >
              {t('admin.exportExcel')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchCategories}>
              {t('common.refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('admin.productCategories.addCategory')}
            </Button>
          </Space>
        }
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          {t('admin.productCategories.description')}
        </p>
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => t('admin.productCategories.totalCategories', { total })
          }}
        />
      </Card>

      <Modal
        title={editingCategory ? t('admin.productCategories.editCategory') : t('admin.productCategories.addCategory')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="name_en"
            label={t('admin.productCategories.nameEn')}
            rules={[
              { required: true, message: t('admin.productCategories.nameEnRequired') },
              { max: 100, message: t('admin.productCategories.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('admin.productCategories.nameEnPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="name_el"
            label={t('admin.productCategories.nameEl')}
            rules={[
              { required: true, message: t('admin.productCategories.nameElRequired') },
              { max: 100, message: t('admin.productCategories.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('admin.productCategories.nameElPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="description_en"
            label={t('admin.productCategories.descriptionEn')}
          >
            <TextArea
              rows={3}
              placeholder={t('admin.productCategories.descriptionEnPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="description_el"
            label={t('admin.productCategories.descriptionEl')}
          >
            <TextArea
              rows={3}
              placeholder={t('admin.productCategories.descriptionElPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('common.status')}
            valuePropName="checked"
          >
            <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCategory ? t('common.update') : t('common.create')}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductCategoryList;
